import { useAuthRedux } from '@/hooks/use-auth-redux';
import { sessionValidator } from '@/lib/session-validator';
import { useEffect, useRef, useState } from 'react';

/**
 * SessionValidator Component
 *
 * This component automatically validates the user session on mount and provides
 * feedback about session status. It's designed to catch cases where Redux thinks
 * the user is authenticated but the actual Supabase session is invalid.
 */
export function SessionValidator() {
  const { isAuthenticated, user, clearSession } = useAuthRedux();
  const [validationStatus, setValidationStatus] = useState<{
    isValidating: boolean;
    lastValidation?: Date;
    error?: string;
  }>({ isValidating: false });

  const hasValidated = useRef(false);

  useEffect(() => {
    // Only validate once when component mounts and user appears to be authenticated
    if (hasValidated.current || !isAuthenticated || !user) {
      return;
    }

    hasValidated.current = true;
    validateCurrentSession();
  }, [isAuthenticated, user]);

  const validateCurrentSession = async () => {
    setValidationStatus({ isValidating: true });

    try {
      console.log('🔍 SessionValidator: Validating current session...');

      const validation = await sessionValidator.validateSession();

      if (validation.isValid) {
        console.log('✅ SessionValidator: Session is valid', {
          user: validation.user?.phone,
          details: validation.details,
        });

        setValidationStatus({
          isValidating: false,
          lastValidation: new Date(),
        });
      } else {
        console.warn('❌ SessionValidator: Session validation failed', {
          error: validation.error,
          details: validation.details,
        });

        setValidationStatus({
          isValidating: false,
          lastValidation: new Date(),
          error: validation.error,
        });

        // Clear the invalid session from Redux
        // This handles the case where Redux thinks user is authenticated
        // but the actual Supabase session is invalid (e.g., after browser refresh)
        clearSession();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ SessionValidator: Validation error:', errorMessage);

      setValidationStatus({
        isValidating: false,
        lastValidation: new Date(),
        error: errorMessage,
      });

      // Clear session on validation error
      clearSession();
    }
  };

  // This component doesn't render anything - it's purely for session validation
  // In development, you can uncomment the debug info below

  if (import.meta.env.DEV && validationStatus.isValidating) {
    console.log('🔍 SessionValidator: Validating session...');
  }

  return null;

  // Uncomment the section below if you want to see validation status in the UI during development
  /*
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 p-2 bg-gray-800 text-white text-xs rounded shadow-lg max-w-xs">
      <div className="font-semibold">Session Validation</div>
      {validationStatus.isValidating && (
        <div className="text-yellow-300">Validating...</div>
      )}
      {validationStatus.lastValidation && (
        <div className="text-gray-300">
          Last checked: {validationStatus.lastValidation.toLocaleTimeString()}
        </div>
      )}
      {validationStatus.error && (
        <div className="text-red-300">Error: {validationStatus.error}</div>
      )}
      {isAuthenticated && user && (
        <div className="text-green-300">
          User: {user.phone}
        </div>
      )}
    </div>
  );
  */
}

/**
 * SessionValidationDebugger Component
 *
 * A debug component that shows session validation status in the UI.
 * Only use this for development/testing purposes.
 */
export function SessionValidationDebugger() {
  const { isAuthenticated, user, loading } = useAuthRedux();
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  const runValidation = async () => {
    setIsValidating(true);
    try {
      const result = await sessionValidator.validateSession();
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsValidating(false);
    }
  };

  const testDatabaseAccess = async () => {
    setIsValidating(true);
    try {
      const result = await sessionValidator.testDatabaseAccess();
      alert(
        `Database access test: ${result.success ? 'SUCCESS' : 'FAILED'}\n${
          result.error || ''
        }`
      );
    } catch (error) {
      alert(
        `Database test error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setIsValidating(false);
    }
  };

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 p-4 bg-white border border-gray-300 rounded shadow-lg max-w-md text-sm">
      <h3 className="font-bold mb-2">Session Validation Debug</h3>

      <div className="space-y-2">
        <div>
          <strong>Redux State:</strong>
          <div>Authenticated: {isAuthenticated ? '✅' : '❌'}</div>
          <div>Loading: {loading ? '⏳' : '✅'}</div>
          <div>User: {user ? `${user.phone}` : 'None'}</div>
        </div>

        <div className="space-x-2">
          <button
            onClick={runValidation}
            disabled={isValidating}
            className="px-2 py-1 bg-blue-500 text-white rounded disabled:opacity-50">
            {isValidating ? 'Validating...' : 'Validate Session'}
          </button>

          <button
            onClick={testDatabaseAccess}
            disabled={isValidating}
            className="px-2 py-1 bg-green-500 text-white rounded disabled:opacity-50">
            Test DB Access
          </button>
        </div>

        {validationResult && (
          <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
            <div>
              <strong>Valid:</strong> {validationResult.isValid ? '✅' : '❌'}
            </div>
            {validationResult.error && (
              <div>
                <strong>Error:</strong> {validationResult.error}
              </div>
            )}
            {validationResult.details && (
              <div className="mt-1">
                <strong>Details:</strong>
                <ul className="ml-2">
                  <li>
                    Supabase Session:{' '}
                    {validationResult.details.hasSupabaseSession ? '✅' : '❌'}
                  </li>
                  <li>
                    Token Valid:{' '}
                    {validationResult.details.isTokenValid ? '✅' : '❌'}
                  </li>
                  <li>
                    DB Access:{' '}
                    {validationResult.details.canAccessDatabase ? '✅' : '❌'}
                  </li>
                  <li>
                    Profile Valid:{' '}
                    {validationResult.details.userProfileValid ? '✅' : '❌'}
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
